import {
  KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors, type Active, type DndContextProps,
  type DragCancelEvent, type DragEndEvent, type DragMoveEvent, type KeyboardSensorOptions,
} from '@dnd-kit/core';
import { locateBlock, type ReportDesignDraft } from '@lib/customization/reportDesign';
import { useLocalization } from '@lib/localization';
import { useCallback, useMemo, useRef, useState, type RefObject } from 'react';
import { definitionOf } from '../blockCatalog';
import { pageLabel } from '../describe';
import {
  collide, keyboardStep, landingOf, placementOf, sideOf, sourceOf, targetOf, type DragSource, type Placement,
} from './dragging';

interface Options {
  /** The draft as it is at the moment of an event, not as it was when the drag began. */
  draft: RefObject<ReportDesignDraft>;
  /** Makes the one edit a drop comes to. Only called for a drop that changes something. */
  onDrop: (source: DragSource, place: Placement) => void;
  /** A block was picked up and let go where a drop changes nothing. Not called for a drag that was called off. */
  onPutBack: (blockId: string) => void;
}

/** A drag in progress, for whoever draws it. */
export interface BlockDrag {
  /** What is being dragged, while something is. */
  carried: DragSource | null;
  /** Where it would land if it were dropped now. `null` where a drop would change nothing. */
  placement: Placement | null;
  /** What the drag context around the palette and the canvas is given. */
  context: Pick<
    DndContextProps,
    | 'sensors' | 'collisionDetection' | 'autoScroll' | 'accessibility'
    | 'onDragStart' | 'onDragMove' | 'onDragOver' | 'onDragEnd' | 'onDragCancel'
  >;
}

// A mouse picks a block up once it has moved this far, so that a click stays a click.
const MOUSE = { activationConstraint: { distance: 6 } };
// A finger once it has rested on the block this long, without straying: a swipe still scrolls the canvas.
const TOUCH = { activationConstraint: { delay: 200, tolerance: 8 } };
// To a place past the middle of the canvas the sensor does not move the block: it scrolls the
// canvas until that place is under it. At once, where the library by itself scrolls smoothly:
// for as long as such a scroll was on its way the block was still over the place before, and
// Space pressed right after the arrow key put it down there. Not `auto` either, which goes by
// the style sheet, and Bootstrap's has the window scroll smoothly.
const KEYBOARD: KeyboardSensorOptions = { coordinateGetter: keyboardStep, scrollBehavior: 'instant' };
// Held near the canvas's upper or lower edge, a drag scrolls it. At the library's own pace a
// whole sheet went by in under half a second, too fast to stop on the page that was meant.
const AUTO_SCROLL = { acceleration: 4, threshold: { x: 0, y: 0.15 } };

const samePlace = (a: Placement | null, b: Placement | null): boolean =>
  a === b || (a !== null && b !== null && a.pageId === b.pageId && a.before === b.before);

/**
 * Keeps the release of a drag that was called off from being a click.
 *
 * The context swallows the click a drop ends in. A drag called off with Escape it stops
 * listening to a moment later, while the mouse button is still down, and letting go of the
 * button after that is an ordinary click to the browser: on the palette entry that was
 * picked up, which added its block after all, or over another block of the page, which
 * selected the page. That click is stopped here, before anything on the page hears it.
 *
 * That one only. A click a pointer makes later begins with a press, and the first press ends
 * the wait; a click from the keyboard, which counts no press (`detail`), is let through.
 */
const swallowRelease = (): void => {
  const swallow = (event: MouseEvent) => {
    if (event.detail === 0) return;

    event.stopPropagation();
    stop();
  };
  const stop = () => {
    document.removeEventListener('click', swallow, true);
    document.removeEventListener('pointerdown', stop, true);
  };

  document.addEventListener('click', swallow, true);
  document.addEventListener('pointerdown', stop, true);
};

/**
 * Dragging blocks onto and between the page sheets: one drag context's worth of sensors,
 * rules and state. The rules themselves are the functions of `dragging.ts`; this only reads
 * the context's events with them.
 *
 * While a drag is on, nothing of the design moves: the canvas draws a line where the block
 * would land and the block itself in an overlay, and the drop is one edit of the draft. The
 * sortable preset would instead make room by shifting the page's other blocks, which here
 * are of very different heights; the blocks under the pointer would change with every shift.
 *
 * A mouse sensor and a touch sensor, not the pointer sensor: that one needs `touch-action:
 * none` on whatever it drags, which here is every block, and then a touch screen could not
 * scroll over a page at all.
 */
export const useBlockDrag = ({ draft, onDrop, onPutBack }: Options): BlockDrag => {
  const { t } = useLocalization();

  const sensors = useSensors(useSensor(MouseSensor, MOUSE), useSensor(TouchSensor, TOUCH), useSensor(KeyboardSensor, KEYBOARD));

  const [carried, setCarried] = useState<DragSource | null>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);
  // The same as the state, for the announcements, which are asked before the next render.
  const placed = useRef<Placement | null>(null);

  const place = useCallback((next: Placement | null) => {
    if (samePlace(placed.current, next)) return;

    placed.current = next;
    setPlacement(next);
  }, []);

  /** Where a drop would land as the context reports the drag now. `undefined` for a report to pass over. */
  const read = useCallback(({ active, over, collisions }: DragMoveEvent): Placement | null | undefined => {
    const side = sideOf(over?.id, collisions);

    // A move comes with what the drag was over a render ago; the over event that follows has both of one moment.
    if (side === null) return undefined;

    return placementOf(draft.current, sourceOf(active.data.current), targetOf(over?.data.current), side === 'after');
  }, [draft]);

  const track = useCallback((event: DragMoveEvent) => {
    const next = read(event);

    if (next !== undefined) place(next);
  }, [read, place]);

  const end = useCallback(() => {
    setCarried(null);
    place(null);
  }, [place]);

  const context = useMemo((): BlockDrag['context'] => {
    /** What is dragged, by the name the editor gives it. */
    const nameOf = (active: Active): string => {
      const source = sourceOf(active.data.current);

      if (!source) return '';

      const type = source.kind === 'palette' ? source.type : locateBlock(draft.current, source.blockId)?.block.type ?? '';
      const definition = definitionOf(type);

      return definition ? t(definition.label) : type;
    };

    /** Where it stands, in words: its place on a page, or that it is where it was. */
    const standing = (active: Active, key: string): string => {
      const source = sourceOf(active.data.current);
      const at = placed.current;

      if (!source || !at) return t('ReportDesign_Drag_Home', [nameOf(active)]);

      const { pageId, index } = landingOf(draft.current, source, at);

      return t(key, [nameOf(active), pageLabel(draft.current, pageId, t), index + 1]);
    };

    return {
      sensors,
      collisionDetection: collide,
      autoScroll: AUTO_SCROLL,
      accessibility: {
        screenReaderInstructions: { draggable: t('ReportDesign_Drag_Instructions') },
        announcements: {
          onDragStart: ({ active }) => t('ReportDesign_Drag_Lifted', [nameOf(active)]),
          onDragOver: ({ active }) => standing(active, 'ReportDesign_Drag_Over'),
          // Said from what `onDragEnd` below has left: the place the block was dropped at.
          onDragEnd: ({ active }) => standing(active, 'ReportDesign_Drag_Dropped'),
          onDragCancel: ({ active }) => t('ReportDesign_Drag_Home', [nameOf(active)]),
        },
      },
      onDragStart: ({ active }) => {
        placed.current = null;
        setCarried(sourceOf(active.data.current));
      },
      onDragMove: track,
      onDragOver: track,
      onDragEnd: (event: DragEndEvent) => {
        const source = sourceOf(event.active.data.current);
        const now = read(event);
        // Where the line is, if the drop itself cannot be read: the block goes where it was shown to go.
        const at = now === undefined ? placed.current : now;

        // Kept for the announcement of the drop, which is asked for right after this.
        placed.current = at;
        setPlacement(null);
        setCarried(null);

        if (source && at) onDrop(source, at);
        // A finger that rests on a block picks it up, and the context swallows the click that
        // lifting it again would have been: without this, a long press selected nothing.
        else if (source?.kind === 'block') onPutBack(source.blockId);
      },
      onDragCancel: ({ activatorEvent }: DragCancelEvent) => {
        end();
        // Called off from the keyboard, a drag has nothing held down to let go of.
        if (activatorEvent.type !== 'keydown') swallowRelease();
      },
    };
  }, [sensors, t, draft, track, read, end, onDrop, onPutBack]);

  return { carried, placement, context };
};
